import { useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Notification } from "../../../../@types/Notification";
import { Button } from "../../../../components/Button";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { useHospitalGroups } from "../../../../hooks/queries/ragConfig";
import { useHospitalGroupWards } from "../../../../hooks/queries/wards";
import { useWardLabel } from "../../../../hooks/useWardLabel";
import { workflowsService } from "../../../../services/worflowsService";
import {
  extractErrors,
  getSummaryErrorMessage,
  isErrorResponse,
} from "../../../../utils/apiErrorUtils";
import { mixpanelActions } from "../../../../utils/mixpanel";
import {
  CancelButton,
  Description,
  ErrorText,
  FieldContainer,
  FieldLabel,
  ModalContainer,
  SubmitContainer,
  Title,
} from "./DuplicateWorkflowModal.styles";

type DuplicateWorkflowModalProps = {
  workflowId: string;
  onClose(notification?: Notification, refetchTable?: boolean): void;
};

export function DuplicateWorkflowModal({
  workflowId,
  onClose,
}: DuplicateWorkflowModalProps) {
  const { t } = useTranslation();

  const wardLabel = useWardLabel();
  const { hospitalGroups } = useHospitalGroups();

  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(
    undefined
  );
  const [selectedWards, setSelectedWards] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { hospitalGroupWards, isWardsFetching } = useHospitalGroupWards(
    selectedGroupId ?? Number.NaN,
    selectedGroupId !== undefined
  );

  const groupOptions: IOption[] = hospitalGroups.map((group) => ({
    label: group.hospitalGroupName,
    value: group.hospitalGroupId.toString(),
    key: uuidv4(),
  }));

  const wardOptions: IOption[] = (hospitalGroupWards ?? []).map((ward) => ({
    label: ward.wardName,
    value: ward.wardId.toString(),
    key: uuidv4(),
  }));

  const handleGroupChange = (value: string | string[]) => {
    const nextGroupId = Number(Array.isArray(value) ? value[0] : value);

    setSelectedGroupId(Number.isNaN(nextGroupId) ? undefined : nextGroupId);
    setSelectedWards([]);
    setErrorMessage("");
  };

  const handleWardsChange = (value: string | string[]) => {
    const ids = Array.isArray(value) ? value : [value];

    setSelectedWards(ids.map((v) => Number(v)).filter((n) => !Number.isNaN(n)));
  };

  const handleDuplicate = async () => {
    if (selectedGroupId === undefined || selectedWards.length === 0) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);
    mixpanelActions.track("User Action: Duplicate workflow");

    try {
      const result = await workflowsService.duplicateWorkflow(
        workflowId,
        selectedWards
      );

      if (isErrorResponse(result)) {
        const errors = extractErrors(result);
        setErrorMessage(
          errors.length
            ? getSummaryErrorMessage(errors)
            : t("workflows_duplicateModal_error")
        );
        setIsLoading(false);
        return;
      }

      const notification: Notification = {
        show: true,
        message: t("workflows_duplicateModal_success"),
        type: "success",
        width: "max-content",
      };

      onClose(notification, true);
    } catch {
      setErrorMessage(t("workflows_duplicateModal_error"));
      setIsLoading(false);
    }
  };

  const isGroupSelected = selectedGroupId !== undefined;

  return (
    <ModalContainer>
      <Title>{t("workflows_duplicateModal_title")}</Title>
      <Description>
        {t("workflows_duplicate_description", {
          wards: wardLabel.plural.toLowerCase(),
        })}
      </Description>

      <FieldContainer>
        <FieldLabel>{t("workflows_duplicateModal_hospital")}</FieldLabel>
        <SelectDropdown
          options={groupOptions}
          width={296}
          height={42}
          placeholder={t("workflows_duplicateModal_selectHospital")}
          showSearch
          optionFilterProp="label"
          value={selectedGroupId?.toString()}
          onValueChange={handleGroupChange}
        />
      </FieldContainer>

      <FieldContainer style={{ marginTop: "16px" }}>
        <FieldLabel>{wardLabel.plural}</FieldLabel>
        <SelectDropdown
          key={selectedGroupId ?? "no-group"}
          options={wardOptions}
          width={296}
          height={42}
          placeholder={
            isGroupSelected
              ? wardLabel.plural
              : t("workflows_duplicateModal_selectHospitalFirst")
          }
          multiple
          disabled={!isGroupSelected || isWardsFetching}
          value={selectedWards.map((id) => id.toString())}
          onValueChange={handleWardsChange}
        />
      </FieldContainer>

      {errorMessage ? <ErrorText>{errorMessage}</ErrorText> : null}

      <SubmitContainer>
        <Button
          label={t("workflows_duplicateModal_submit")}
          type="submit"
          onClick={handleDuplicate}
          isLoading={isLoading}
          disabled={!isGroupSelected || selectedWards.length === 0}
        />
        <CancelButton onClick={() => onClose()}>
          {t("common_cancel")}
        </CancelButton>
      </SubmitContainer>
    </ModalContainer>
  );
}

export default DuplicateWorkflowModal;
